/**
 * Module dependencies.
 */

import {
  CarouselContainer,
  Category,
  Content,
  Description,
  Image,
  MobileOverlay,
  Title,
} from './home-carousel.style';

import { JSX, useCallback, useEffect, useRef, useState } from 'react';

/**
 * `Props` type.
 */

type Props = {
  category: string;
  children?: JSX.Element;
  color: string;
  current: number;
  description: string;
  imageSrc: string;
  max: number;
  onContentPosition: (config: any) => void;
  slideIndex: number;
  title: string;
};

/**
 * Export `Slide` component.
 */

export function Slide(props: Props): JSX.Element {
  const {
    category,
    children,
    color,
    description,
    imageSrc,
    max,
    onContentPosition,
    slideIndex,
    title,
  } = props;

  const categoryRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isStart, setIsStart] = useState(false);

  const current = ((props.current % max) + max) % max;
  const normalizedSlideIndex = slideIndex % max;

  let slideDiff;

  if (normalizedSlideIndex === 0 && current === max - 1) {
    slideDiff = 1;
  } else if (normalizedSlideIndex === max - 1 && current === 0) {
    slideDiff = -1;
  } else {
    slideDiff = normalizedSlideIndex - current;
  }

  useEffect(() => {
    if (!isStart) {
      return;
    }

    if (categoryRef.current) {
      categoryRef.current.style.opacity =
        normalizedSlideIndex === current ? '1' : '0';
      categoryRef.current.style.transform = `scale(${slideDiff * 0.2 + 1})`;
    }

    if (titleRef.current) {
      titleRef.current.style.opacity =
        normalizedSlideIndex === current ? '1' : '0';
      titleRef.current.style.transform = `scale(${slideDiff * 0.2 + 1})`;
    }

    if (descriptionRef.current) {
      descriptionRef.current.style.transform = `translateY(${slideDiff * 30}px) scale(${slideDiff * 0.2 + 1})`;
      descriptionRef.current.style.opacity =
        normalizedSlideIndex === current ? '1' : '0';
    }
  }, [current, isStart, normalizedSlideIndex, slideDiff]);

  useEffect(() => {
    setIsLoaded(true);

    const timer = setTimeout(() => {
      setIsStart(true);
      clearTimeout(timer);
    }, 400);
  }, []);

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const updatePosition = useCallback(() => {
    if (wrapperRef.current && contentRef.current) {
      const wrapperRect = wrapperRef.current.getBoundingClientRect();
      const contentRect = contentRef.current.getBoundingClientRect();

      onContentPosition({
        height: contentRef.current.offsetHeight,
        left: contentRect.left - wrapperRect.left,
        top: contentRect.top - wrapperRect.top,
        width: contentRef.current.offsetWidth,
      });
    }
  }, [onContentPosition]);

  useEffect(() => {
    updatePosition();

    window.addEventListener('resize', updatePosition);
    const observer = new ResizeObserver(updatePosition);

    if (contentRef.current) {
      observer.observe(contentRef.current);
    }

    return () => {
      window.removeEventListener('resize', updatePosition);
      observer.disconnect();
    };
  }, [updatePosition]);

  return (
    <CarouselContainer ref={wrapperRef}>
      <Image
        alt={title}
        left={slideIndex < current}
        right={slideIndex > current}
        src={imageSrc}
        visible={slideIndex === current}
      />

      <MobileOverlay />

      <Content ref={contentRef}>
        {isLoaded && (
          <>
            <Category color={color} ref={categoryRef}>
              {category}
            </Category>

            <Title ref={titleRef}>{title}</Title>

            <Description ref={descriptionRef}>{description}</Description>
          </>
        )}
      </Content>

      {children}
    </CarouselContainer>
  );
}
